import { DropdownItem } from "@flowtribe/ui";

import { AssigneeType, LABEL_CHIP_NAME_MAX_LENGTH, LABEL_TOOLTIP_MAX_NAMES, StepAssigneeMode } from "../../enums/review-workflow.enum";
import { AssigneeEntry, AssigneeOptionValue, StepCondition } from "../../interfaces/review-workflow.interface";
import { AssignmentFormStep, StepCandidateUser } from "../../interfaces/review-workflow-instance.interface";
import { CONDITION_OPTIONS } from "../../utils/step-condition.util";

/** Prints an `AssigneeEntry`. An unpopulated id degrades to "unknown assignee" — never a raw Mongo id. */

export type Translator = (key: string, params?: Record<string, unknown>) => string;

const SPECIAL_KEY_PREFIX = "tasks.special_assignee_";

/** A single user who cannot reach the customer. */
export function markNoAccess(name: string, translate: Translator): string {
  return translate("add-task-workflow-step.No_customer_access_suffix", { name });
}

/** A label or special group is flagged as soon as any member cannot reach the customer, and those members are named. */
export function markNoAccessMembers(name: string, noAccessNames: readonly string[], translate: Translator): string {
  if (!noAccessNames.length) return name;
  return translate("add-task-workflow-step.No_customer_access_members", { name, members: noAccessNames.join(", ") });
}

export function describeAssignee(entry: AssigneeEntry, translate: Translator): string {
  switch (entry.type) {
    case AssigneeType.SPECIAL:
      return entry.special ? translate(SPECIAL_KEY_PREFIX + entry.special) : translate("tasks.special_assignee_unknown");

    case AssigneeType.LABEL:
    case AssigneeType.ADVANCE_LABEL:
      return entry.label ? labelDisplayName(entry.label) : translate("tasks.special_assignee_unknown");

    case AssigneeType.USER:
      return userName(entry) ?? translate("tasks.special_assignee_unknown");

    default:
      return translate("tasks.special_assignee_unknown");
  }
}

export function describeAssignees(entries: AssigneeEntry[] | undefined, translate: Translator): string[] {
  if (!entries?.length) return [];
  return entries.map(entry => describeAssignee(entry, translate));
}

interface PoolGroup {
  label: string;
  isUser: boolean;
  noAccessNames: string[];
}

/**
 * One entry per user, label or special; candidates reached through the same label or special collapse into one.
 * What cannot reach the customer is marked: a user by its own name, a label or special by the members without access.
 */
export function describeCandidatePool(candidates: StepCandidateUser[] | undefined, translate: Translator): string {
  if (!candidates?.length) return translate("tasks.special_assignee_unknown");

  const groups = new Map<string, PoolGroup>();
  candidates.forEach(candidate => {
    const source = candidate.source;
    const isLabelOrSpecial =
      source.type === AssigneeType.SPECIAL || source.type === AssigneeType.LABEL || source.type === AssigneeType.ADVANCE_LABEL;
    const key = isLabelOrSpecial ? `${source.type}:${source.special ?? source.label ?? ""}` : `user:${candidate._id}`;

    let group = groups.get(key);
    if (!group) {
      let label: string;
      if (source.type === AssigneeType.SPECIAL) {
        label = source.special ? translate(SPECIAL_KEY_PREFIX + source.special) : translate("tasks.special_assignee_unknown");
      } else if (isLabelOrSpecial) {
        label = source.label ? labelDisplayName(source.label) : translate("tasks.special_assignee_unknown");
      } else {
        label = candidate.name || translate("tasks.special_assignee_unknown");
      }
      group = { label, isUser: !isLabelOrSpecial, noAccessNames: [] };
      groups.set(key, group);
    }
    if (!candidate.hasCustomerAccess) group.noAccessNames.push(candidate.name);
  });

  return [...groups.values()]
    .map(({ label, isUser, noAccessNames }) =>
      isUser && noAccessNames.length ? markNoAccess(label, translate) : markNoAccessMembers(label, noAccessNames, translate)
    )
    .join(", ");
}

export function describeStepPool(step: AssignmentFormStep, translate: Translator): string {
  if (step.assigneeMode === StepAssigneeMode.ALL_PREVIOUS_STEPS) {
    return translate("tasks.steps_all_previous_assignees");
  }
  return describeCandidatePool(step.candidateUsers, translate);
}

/**
 * Candidates of a step that cannot reach the customer. An editable step only counts the picks made so far;
 * a preconfigured step counts its whole pool, since every one of them is assigned.
 */
export function stepNoAccessCandidates(step: AssignmentFormStep, selectedUserIds: readonly string[] = []): StepCandidateUser[] {
  const candidates = step.candidateUsers ?? [];
  const relevant = step.editable ? candidates.filter(candidate => selectedUserIds.includes(candidate._id)) : candidates;
  return relevant.filter(candidate => !candidate.hasCustomerAccess);
}

export function describeStepConditions(conditions: StepCondition[] | undefined, translate: Translator): string | null {
  if (!conditions?.length) return null;

  const labels: string[] = [];
  for (const condition of conditions) {
    const option = CONDITION_OPTIONS.find(candidate => candidate.field === condition.field && candidate.value === condition.value);
    if (!option) return null;
    labels.push(translate(option.labelKey));
  }
  return translate("tasks.add_step_conditional_hint_conditions", { value: joinWithOr(labels, translate) });
}

function joinWithOr(labels: string[], translate: Translator): string {
  if (labels.length <= 1) return labels[0] ?? "";
  const or = translate("tasks.add_step_conditional_hint_join_or");
  return `${labels.slice(0, -1).join(", ")} ${or} ${labels[labels.length - 1]}`;
}

/**
 * Chip text for `ui-dropdown-multiselect-with-chips`. Its chip has no width cap of its own, so a long name wraps out of
 * the fixed-height chip or pushes the remove button out of reach. Clipping in CSS would leave the chip unremovable, so
 * the cap goes on the string.
 */
export function toChipName(text: string, count?: number): string {
  const shortened = text.length > LABEL_CHIP_NAME_MAX_LENGTH ? `${text.slice(0, LABEL_CHIP_NAME_MAX_LENGTH - 1).trimEnd()}…` : text;
  return count === undefined ? shortened : `${shortened} (${count})`;
}

/**
 * Advanced labels are stored as `field:value`; an assignee label ending in ':' (`field:`) names every value of the field.
 * Mirrors the backend's `userHasLabel`.
 */
export function labelCovers(label: string, userLabel: string): boolean {
  return label.endsWith(":") ? userLabel.startsWith(label) : userLabel === label;
}

/** A field-wide label is stored as `field:` but reads as `field`. */
export function labelDisplayName(label: string): string {
  return label.replace(/:$/, "");
}

/**
 * The option `name` of an offered label. `ui-dropdown-multiselect-with-chips` matches options and chips by name, so a
 * field-wide `field:` that shares its bare name with an offered plain label `field` is told apart ("field (all values)");
 * otherwise it reads as the bare field. The chip text is separate (`chipName`) and always the bare name.
 */
export function labelOptionName(label: string, offeredLabels: readonly string[], translate: Translator): string {
  const name = labelDisplayName(label);
  return label.endsWith(":") && offeredLabels.includes(name)
    ? translate("review-workflow.Assignee_label_field_all", { field: name })
    : name;
}

export function labelMemberNames(label: string, users: readonly { name: string; labels?: string[] }[]): string[] {
  return users.filter(user => (user.labels ?? []).some(userLabel => labelCovers(label, userLabel))).map(user => user.name);
}

/** A label can cover a whole firm, so its hover names the first members only. */
export function membersTooltip(names: readonly string[]): string | undefined {
  if (!names.length) return undefined;
  const shown = names.slice(0, LABEL_TOOLTIP_MAX_NAMES).join(", ");
  return names.length > LABEL_TOOLTIP_MAX_NAMES ? `${shown}, …` : shown;
}

/**
 * The option for a stored label. A label that is no longer offered (e.g. an older per-value `field:value`) still shows
 * as a chip while someone holds it; one nobody holds any more is dropped, as a vanished user is.
 */
export function resolveLabelItem(
  items: readonly DropdownItem<AssigneeOptionValue>[],
  users: readonly { name: string; labels?: string[] }[],
  assignee: { type: AssigneeType; label: string }
): DropdownItem<AssigneeOptionValue> | undefined {
  const offered = items.find(item => item.item.label === assignee.label);
  if (offered) return offered;

  const members = labelMemberNames(assignee.label, users);
  if (!members.length) return undefined;
  const name = labelDisplayName(assignee.label);
  return {
    name,
    chipName: toChipName(name, members.length),
    tooltipMessage: membersTooltip(members),
    item: { type: assignee.type, label: assignee.label },
    icon: "Tag"
  };
}

export function assigneeUserId(entry: AssigneeEntry): string | null {
  if (entry.type !== AssigneeType.USER || !entry.user) return null;
  return typeof entry.user === "string" ? entry.user : entry.user._id;
}

function userName(entry: AssigneeEntry): string | null {
  if (!entry.user || typeof entry.user === "string") return null;
  return entry.user.name || null;
}
